"use client";

import { cn } from "@/lib/utils";
import gsap from "gsap";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface SplashScreenProps {
  onComplete: () => void;
  duration?: number; // Minimum time the splash should run (ms)
  scale?: number; // Max scale during pulse
  logoSrc?: string; // Image to show
  initialScale?: number; // Starting scale of the logo
}

export function SplashScreenV2({
  onComplete,
  duration = 4000,
  scale = 1.08,
  logoSrc = "/images/logo/atc-logo-full-transparent.png",
  initialScale = 0.5,
}: SplashScreenProps) {
  // ## define Ref
  const splashRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  // Create an overlay to simulate the black center expanding
  const overlayRef = useRef<HTMLDivElement>(null); // Add this ref in your component
  // ## define state
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [animationReady, setAnimationReady] = useState(false); // guards main animation

  const bounceTlRef = useRef<gsap.core.Timeline | null>(null);

  // ⌛ Minimum timer: only once
  useEffect(() => {
    const timer = setTimeout(() => setMinTimePassed(true), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  // ⏳ Image loading check: only once
  useEffect(() => {
    if (typeof window === "undefined") return;

    const images = Array.from(document.querySelectorAll("img"));
    let loadedCount = 0;

    const imageLoaded = () => {
      loadedCount++;
      if (loadedCount === images.length) {
        setImagesLoaded(true);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        imageLoaded();
      } else {
        img.addEventListener("load", imageLoaded);
        img.addEventListener("error", imageLoaded); // Count errors as "loaded" to prevent hanging
      }
    });

    // Cleanup event listeners
    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", imageLoaded);
        img.removeEventListener("error", imageLoaded);
      });
    };
  }, []);

  // Run animation once on mount
  useEffect(() => {
    if (!splashRef.current || !logoRef.current) return;

    const tl = gsap.timeline({
      defaults: { ease: "power2.out" },
    });

    // Initial fade + zoom in
    tl.set(splashRef.current, { opacity: 1 });

    // tl.set(logoRef.current, {visibility: 'visible', scale: initialScale, filter: 'blur(20px)'})

    // TODO: there is still a slight jump when the animation starts

    tl.from(logoRef.current, {
      visibility: "visible",
      opacity: 1,
      scale: initialScale,
      filter: "blur(20px)",
      ease: "power2.out",
      // TODO: figure out perfect duration...
      // duration: 0.5,
      duration: 0.3,
    });

    tl.to(logoRef.current, {
      scale: scale,
      opacity: 1,
      filter: "blur(0px)",
      // TODO: figure out perfect duration...
      // duration: 1
      duration: 0.6,
    });

    // Bounce loop (stored separately)
    const bounceTl = gsap.timeline({
      repeat: -1,
      yoyo: true,
      paused: true,
      defaults: { ease: "sine.inOut", duration: 1.2 },
    });

    bounceTl.to(logoRef.current, { scale });
    bounceTl.play();
    bounceTlRef.current = bounceTl;

    setAnimationReady(true); // allows exit logic to run

    return () => {
      tl.kill();
      bounceTl.kill();
    };
  }, [initialScale, scale]);

  // ✅ Exit when both conditions are true
  useEffect(() => {
    if (imagesLoaded && minTimePassed && animationReady) {
      const bounceTl = bounceTlRef.current;

      if (!bounceTl) return;

      bounceTl.eventCallback("onRepeat", () => {
        bounceTl?.kill();

        const exitTl = gsap.timeline({
          defaults: { ease: "power2.out" },
          onComplete: () => onComplete(),
        });

        // Animate logo zoom into its center
        exitTl.to(
          logoRef.current,
          {
            scale: 15,
            // TODO: figure out perfect duration...
            // duration: 1.2,
            duration: 0.8,
            ease: "power3.inOut",
            transformOrigin: "50% 50%",
          },
          0,
        );

        // Transition background to black and scale an overlay from the logo center
        exitTl.to(
          splashRef.current,
          {
            backgroundColor: "#000",
            // TODO: figure out perfect duration...
            // duration: 1.2,
            duration: 0.6,
            ease: "power3.inOut",
          },
          0,
        );

        exitTl.fromTo(
          overlayRef.current,
          { scale: 0, opacity: 0, backgroundColor: "#000" },
          {
            scale: 20,
            opacity: 1,
            // TODO: figure out perfect duration...
            // duration: 1.2,
            duration: 0.6,
            ease: "power3.inOut",
            transformOrigin: "50% 50%",
          },
          0,
        );

        // Fade out the splash screen after the zoom and transition
        exitTl.to(splashRef.current, {
          opacity: 0,
          // TODO: figure out perfect duration...
          // duration: 0.6,
          duration: 0.3,
          delay: 0.2,
          onComplete: () => {
            splashRef.current!.style.display = "none";
            if (overlayRef.current) overlayRef.current.style.display = "none";
          },
        });
      });
    }
  }, [imagesLoaded, minTimePassed, animationReady, onComplete]);

  return (
    <div
      ref={splashRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white"
      style={{ backgroundColor: "#fff" }}
    >
      <div
        ref={logoRef}
        className={cn(
          "relative h-32 w-64 md:h-40 md:w-80",
          // "transform -translate-x-1/2 animate-bounce"
        )}
        style={{
          opacity: 0,
          transform: `scale(${initialScale})`,
          filter: "blur(20px)",
        }}
      >
        <Image
          src={logoSrc}
          alt="Acquired Taste Comedy"
          fill
          sizes="(max-width: 768px) 256px, 320px"
          style={{
            objectFit: "contain",
          }}
          priority
          // loading="eager"
        />
      </div>
      <div ref={overlayRef} className="absolute inset-0 z-40" style={{ display: "none" }} />
    </div>
  );
}
